/**
 * PlatformTable Component
 *
 * One row per connected platform: spend, results, cost per result, click
 * rate and whether its data is up to date. Clicking a row filters the whole
 * dashboard to that platform; clicking it again clears the filter.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import type { AdPlatform, PlatformSummary, PlatformSyncStatus } from '@/types/analytics';
import { useTheme } from '@/hooks/useTheme';
import {
  PLATFORM_COLOR,
  PLATFORM_LABEL,
  formatCount,
  formatMoney,
  formatPct,
  resultsBreakdown,
  timeAgo,
} from '@/utils/analyticsFormat';

export interface PlatformTableProps {
  platforms: AdPlatform[];
  byPlatform: Partial<Record<AdPlatform, PlatformSummary>>;
  sync: Partial<Record<AdPlatform, PlatformSyncStatus>>;
  selected: AdPlatform | null;
  onSelect: (platform: AdPlatform | null) => void;
}

const SYNC_TEXT: Record<PlatformSyncStatus['state'], { text: (s: PlatformSyncStatus) => string; tone: 'ok' | 'warn' | 'muted' }> = {
  ok: { text: (s) => `Synced ${timeAgo(s.last_synced_at)}`, tone: 'ok' },
  stale: { text: (s) => `Last synced ${timeAgo(s.last_synced_at)}`, tone: 'warn' },
  error: { text: () => 'Sync failed', tone: 'warn' },
  reauth: { text: () => 'Reconnect needed', tone: 'warn' },
  syncing: { text: () => 'First sync running', tone: 'muted' },
  pending: { text: () => 'Waiting for first sync', tone: 'muted' },
  paused: { text: () => 'Sync paused', tone: 'muted' },
  disconnected: { text: () => 'Disconnected', tone: 'warn' },
};

export const SyncLine: React.FC<{ status?: PlatformSyncStatus }> = ({ status }) => {
  const navigate = useNavigate();
  if (!status) return null;
  const { text, tone } = SYNC_TEXT[status.state];
  const fixable = status.state === 'reauth' || status.state === 'disconnected';
  return (
    <span
      className={`inline-flex items-center gap-1 text-[11px] ${tone === 'warn' ? 'text-warning' : 'text-ink-3'}`}
      title={status.message ?? undefined}
    >
      {tone === 'warn' ? (
        <svg className="w-3 h-3" fill="none" stroke="currentColor" strokeWidth={2.2} viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v4m0 4h.01M10.3 3.9L1.8 18a2 2 0 001.7 3h17a2 2 0 001.7-3L13.7 3.9a2 2 0 00-3.4 0z" />
        </svg>
      ) : tone === 'ok' ? (
        <span className="w-1.5 h-1.5 rounded-full bg-success" aria-hidden="true" />
      ) : null}
      {text(status)}
      {fixable && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); navigate('/settings?tab=connections'); }}
          className="ml-1 text-[11px] font-bold underline hover:no-underline"
        >
          Fix
        </button>
      )}
    </span>
  );
};

export const PlatformTable: React.FC<PlatformTableProps> = ({ platforms, byPlatform, sync, selected, onSelect }) => {
  const { theme } = useTheme();
  const colors = PLATFORM_COLOR[theme === 'dark' ? 'dark' : 'light'];

  return (
    <div className="overflow-hidden rounded-panel bg-card">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[560px] text-left">
          <thead>
            <tr className="border-b border-border text-[11px] font-bold uppercase tracking-wider text-ink-3">
              <th className="px-4 sm:px-[18px] py-3">Platform</th>
              <th className="px-4 sm:px-[18px] py-3 text-right">Spend</th>
              <th className="px-4 sm:px-[18px] py-3 text-right">Results</th>
              <th className="px-4 sm:px-[18px] py-3 text-right">Cost / result</th>
              <th className="px-4 sm:px-[18px] py-3 text-right">Click rate</th>
              <th className="px-4 sm:px-[18px] py-3 text-right">Delivering</th>
            </tr>
          </thead>
          <tbody>
            {platforms.map((platform) => {
              const s = byPlatform[platform];
              const isSelected = selected === platform;
              const noTracking = s?.tracking_gap;
              return (
                <tr
                  key={platform}
                  onClick={() => onSelect(isSelected ? null : platform)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      onSelect(isSelected ? null : platform);
                    }
                  }}
                  tabIndex={0}
                  aria-pressed={isSelected}
                  className={`border-b border-border last:border-b-0 cursor-pointer transition-colors tabular-nums
                    ${isSelected ? 'bg-primary-soft' : 'hover:bg-surface/60'}`}
                >
                  <td className="px-4 sm:px-[18px] py-3">
                    <div className="flex items-center gap-2.5">
                      <span className="w-2.5 h-2.5 rounded-[3px] shrink-0" style={{ background: colors[platform] }} />
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-ink">{PLATFORM_LABEL[platform]}</span>
                        <SyncLine status={sync[platform]} />
                      </div>
                    </div>
                  </td>
                  {s ? (
                    <>
                      <td className="px-4 sm:px-[18px] py-3 text-right text-[13px] font-semibold text-ink">
                        {formatMoney(s.spend, s.currency)}
                      </td>
                      <td className="px-4 sm:px-[18px] py-3 text-right">
                        {noTracking ? (
                          <span className="text-[12px] text-warning">No conversions tracked</span>
                        ) : s.conversions === 0 ? (
                          <span className="text-[13px] text-ink-3">—</span>
                        ) : (
                          <div className="flex flex-col items-end">
                            <span className="text-[13px] font-semibold text-ink">{formatCount(s.conversions)}</span>
                            <span className="text-[11px] text-ink-3">{resultsBreakdown(s.results)}</span>
                          </div>
                        )}
                      </td>
                      <td className="px-4 sm:px-[18px] py-3 text-right text-[13px] text-ink-2">
                        {formatMoney(s.cost_per_result, s.currency)}
                      </td>
                      <td className="px-4 sm:px-[18px] py-3 text-right text-[13px] text-ink-2">{formatPct(s.ctr)}</td>
                      <td className="px-4 sm:px-[18px] py-3 text-right text-[13px] text-ink-2">
                        {s.active_campaigns} {s.active_campaigns === 1 ? 'campaign' : 'campaigns'}
                      </td>
                    </>
                  ) : (
                    <td colSpan={5} className="px-4 sm:px-[18px] py-3 text-right text-[13px] text-ink-3">
                      No delivery in this period
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default PlatformTable;
